// components/AdvancedRichTextEditor.jsx
import React, { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import Underline from '@tiptap/extension-underline';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import FontFamily from '@tiptap/extension-font-family';
import Typography from '@tiptap/extension-typography';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Dropcursor from '@tiptap/extension-dropcursor';
import Gapcursor from '@tiptap/extension-gapcursor';
import { createLowlight } from 'lowlight';
import javascript from 'highlight.js/lib/languages/javascript';
import python from 'highlight.js/lib/languages/python';
import css from 'highlight.js/lib/languages/css';
import html from 'highlight.js/lib/languages/xml';
import json from 'highlight.js/lib/languages/json';
import bash from 'highlight.js/lib/languages/bash';

import {
  Box,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  Menu,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Popover,
  Typography as MuiTypography,
  Stack,
} from '@mui/material';

// Import icons correctly
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import CodeIcon from '@mui/icons-material/Code';
import TableChartIcon from '@mui/icons-material/TableChart';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import ClearIcon from '@mui/icons-material/Clear';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import RedoIcon from '@mui/icons-material/Redo';
import UndoIcon from '@mui/icons-material/Undo';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// Initialize lowlight with languages
const lowlight = createLowlight();
lowlight.register({ javascript, python, css, html, json, bash });

const AdvancedRichTextEditor = ({ value, onChange, placeholder = 'Write your blog content here...' }) => {
  const [linkUrl, setLinkUrl] = useState('');
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [colorAnchorEl, setColorAnchorEl] = useState(null);
  const [highlightAnchorEl, setHighlightAnchorEl] = useState(null);
  const [fontFamilyAnchorEl, setFontFamilyAnchorEl] = useState(null);
  const [fontSizeAnchorEl, setFontSizeAnchorEl] = useState(null);
  const [fontSize, setFontSize] = useState(16);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false,
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Highlight,
      Underline,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      CodeBlockLowlight.configure({
        lowlight,
      }),
      Color,
      TextStyle,
      FontFamily,
      Typography,
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      HorizontalRule,
      Dropcursor,
      Gapcursor,
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'advanced-editor-content',
        style: `font-size: ${fontSize}px;`,
      },
    },
  });

  // Image upload handler
  const handleImageUpload = async (file) => {
    if (!editor) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      editor.chain().focus().setImage({ src: e.target.result }).run();
    };
    reader.readAsDataURL(file);
  };

  const addLink = () => {
    if (!editor) return;
    const { from, to } = editor.state.selection;
    if (from !== to && linkUrl) {
      editor.chain().focus().setLink({ href: linkUrl }).run();
    }
    setLinkDialogOpen(false);
    setLinkUrl('');
  };

  const addImage = () => {
    if (!editor) return;
    if (imageUrl) {
      editor.chain().focus().setImage({ src: imageUrl }).run();
      setImageDialogOpen(false);
      setImageUrl('');
    } else if (imageFile) {
      handleImageUpload(imageFile);
      setImageDialogOpen(false);
      setImageFile(null);
    }
  };

  const addTable = () => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const setFontFamilyValue = (font) => {
    if (!editor) return;
    editor.chain().focus().setFontFamily(font).run();
    setFontFamilyAnchorEl(null);
  };

  const setFontSizeValue = (size) => {
    setFontSize(size);
    if (!editor) return;
    editor.chain().focus().setMark('textStyle', { fontSize: `${size}px` }).run();
    setFontSizeAnchorEl(null);
  };

  const MenuButton = ({ tooltip, onClick, active, children }) => (
    <Tooltip title={tooltip}>
      <IconButton
        onClick={onClick}
        color={active ? 'primary' : 'default'}
        size="small"
        sx={{ 
          borderRadius: 1,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {children}
      </IconButton>
    </Tooltip>
  );

  if (!editor) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      {/* Toolbar */}
      <Box sx={{ 
        borderBottom: 1, 
        borderColor: 'divider', 
        p: 1, 
        bgcolor: 'background.default',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 1,
      }}>
        {/* Text Formatting */}
        <MenuButton
          tooltip="Bold (Ctrl+B)"
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
        >
          <FormatBoldIcon />
        </MenuButton>

        <MenuButton
          tooltip="Italic (Ctrl+I)"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
        >
          <FormatItalicIcon />
        </MenuButton>

        <MenuButton
          tooltip="Underline (Ctrl+U)"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
        >
          <FormatUnderlinedIcon />
        </MenuButton>

        <MenuButton
          tooltip="Strikethrough"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive('strike')}
        >
          <FormatStrikethroughIcon />
        </MenuButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Text Color */}
        <MenuButton
          tooltip="Text Color"
          onClick={(e) => setColorAnchorEl(e.currentTarget)}
        >
          <FormatColorTextIcon />
        </MenuButton>

        <Popover
          open={Boolean(colorAnchorEl)}
          anchorEl={colorAnchorEl}
          onClose={() => setColorAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap', width: 250 }}>
            {['#000000', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#808080', '#800000', '#808000', '#008000', '#800080', '#008080', '#000080'].map(color => (
              <Box
                key={color}
                onClick={() => {
                  editor.chain().focus().setColor(color).run();
                  setColorAnchorEl(null);
                }}
                sx={{
                  width: 35,
                  height: 35,
                  bgcolor: color,
                  cursor: 'pointer',
                  borderRadius: 1,
                  border: '1px solid #ddd',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'scale(1.1)' },
                }}
              />
            ))}
            <Button 
              size="small" 
              fullWidth 
              onClick={() => {
                editor.chain().focus().unsetColor().run();
                setColorAnchorEl(null);
              }}
            >
              Remove Color
            </Button>
          </Box>
        </Popover>

        {/* Highlight */}
        <MenuButton
          tooltip="Highlight"
          onClick={(e) => setHighlightAnchorEl(e.currentTarget)}
        >
          <FormatColorFillIcon />
        </MenuButton>

        <Popover
          open={Boolean(highlightAnchorEl)}
          anchorEl={highlightAnchorEl}
          onClose={() => setHighlightAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap', width: 250 }}>
            {['#FFEB3B', '#FFC107', '#FF9800', '#FF5722', '#4CAF50', '#2196F3', '#9C27B0', '#F44336', '#E91E63', '#00BCD4'].map(color => (
              <Box
                key={color}
                onClick={() => {
                  editor.chain().focus().toggleHighlight({ color }).run();
                  setHighlightAnchorEl(null);
                }}
                sx={{
                  width: 35,
                  height: 35,
                  bgcolor: color,
                  cursor: 'pointer',
                  borderRadius: 1,
                  border: '1px solid #ddd',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'scale(1.1)' },
                }}
              />
            ))}
            <Button 
              size="small" 
              fullWidth 
              onClick={() => {
                editor.chain().focus().unsetHighlight().run();
                setHighlightAnchorEl(null);
              }}
            >
              Remove Highlight
            </Button>
          </Box>
        </Popover>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Font Family */}
        <>
          <Button
            size="small"
            onClick={(e) => setFontFamilyAnchorEl(e.currentTarget)}
            endIcon={<KeyboardArrowDownIcon />}
            sx={{ textTransform: 'none' }}
          >
            Font
          </Button>
          <Menu
            anchorEl={fontFamilyAnchorEl}
            open={Boolean(fontFamilyAnchorEl)}
            onClose={() => setFontFamilyAnchorEl(null)}
          >
            <MenuItem onClick={() => setFontFamilyValue('Arial')}>Arial</MenuItem>
            <MenuItem onClick={() => setFontFamilyValue('Georgia')}>Georgia</MenuItem>
            <MenuItem onClick={() => setFontFamilyValue('Times New Roman')}>Times New Roman</MenuItem>
            <MenuItem onClick={() => setFontFamilyValue('Courier New')}>Courier New</MenuItem>
            <MenuItem onClick={() => setFontFamilyValue('Verdana')}>Verdana</MenuItem>
            <MenuItem onClick={() => setFontFamilyValue('Impact')}>Impact</MenuItem>
          </Menu>
        </>

        {/* Font Size */}
        <>
          <Button
            size="small"
            onClick={(e) => setFontSizeAnchorEl(e.currentTarget)}
            endIcon={<KeyboardArrowDownIcon />}
            sx={{ textTransform: 'none' }}
          >
            Size
          </Button>
          <Menu
            anchorEl={fontSizeAnchorEl}
            open={Boolean(fontSizeAnchorEl)}
            onClose={() => setFontSizeAnchorEl(null)}
          >
            {[10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48].map(size => (
              <MenuItem key={size} onClick={() => setFontSizeValue(size)}>
                {size}px
              </MenuItem>
            ))}
          </Menu>
        </>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Alignment */}
        <ToggleButtonGroup size="small">
          <ToggleButton
            value="left"
            selected={editor.isActive({ textAlign: 'left' })}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
          >
            <FormatAlignLeftIcon />
          </ToggleButton>
          <ToggleButton
            value="center"
            selected={editor.isActive({ textAlign: 'center' })}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
          >
            <FormatAlignCenterIcon />
          </ToggleButton>
          <ToggleButton
            value="right"
            selected={editor.isActive({ textAlign: 'right' })}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
          >
            <FormatAlignRightIcon />
          </ToggleButton>
          <ToggleButton
            value="justify"
            selected={editor.isActive({ textAlign: 'justify' })}
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          >
            <FormatAlignJustifyIcon />
          </ToggleButton>
        </ToggleButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Lists */}
        <MenuButton
          tooltip="Bullet List"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
        >
          <FormatListBulletedIcon />
        </MenuButton>

        <MenuButton
          tooltip="Numbered List"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
        >
          <FormatListNumberedIcon />
        </MenuButton>

        <MenuButton
          tooltip="Task List"
          onClick={() => editor.chain().focus().toggleTaskList().run()}
          active={editor.isActive('taskList')}
        >
          <CheckBoxIcon />
        </MenuButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Block Elements */}
        <MenuButton
          tooltip="Quote"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive('blockquote')}
        >
          <FormatQuoteIcon />
        </MenuButton>

        <MenuButton
          tooltip="Code Block"
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          active={editor.isActive('codeBlock')}
        >
          <CodeIcon />
        </MenuButton>

        <MenuButton
          tooltip="Horizontal Rule"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        >
          <HorizontalRuleIcon />
        </MenuButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Media */}
        <MenuButton
          tooltip="Add Image"
          onClick={() => setImageDialogOpen(true)}
        >
          <InsertPhotoIcon />
        </MenuButton>

        <MenuButton
          tooltip="Add Link"
          onClick={() => setLinkDialogOpen(true)}
          active={editor.isActive('link')}
        >
          <InsertLinkIcon />
        </MenuButton>

        <MenuButton
          tooltip="Add Table"
          onClick={addTable}
        >
          <TableChartIcon />
        </MenuButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* History */}
        <MenuButton
          tooltip="Undo (Ctrl+Z)"
          onClick={() => editor.chain().focus().undo().run()}
        >
          <UndoIcon />
        </MenuButton>

        <MenuButton
          tooltip="Redo (Ctrl+Y)"
          onClick={() => editor.chain().focus().redo().run()}
        >
          <RedoIcon />
        </MenuButton>

        <MenuButton
          tooltip="Clear Formatting"
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
        >
          <ClearIcon />
        </MenuButton>
      </Box>

      {/* Editor Content */}
      <Box sx={{ 
        '& .advanced-editor-content': {
          minHeight: 500,
          maxHeight: '70vh',
          overflowY: 'auto',
          padding: 3,
          '&:focus': {
            outline: 'none',
          },
          '& img': {
            maxWidth: '100%',
            height: 'auto',
            cursor: 'pointer',
            borderRadius: '4px',
          },
          '& table': {
            borderCollapse: 'collapse',
            width: '100%',
            margin: '1em 0',
            '& td, & th': {
              border: '1px solid #ddd',
              padding: '8px',
              position: 'relative',
            },
            '& th': {
              backgroundColor: '#f5f5f5',
              fontWeight: 'bold',
            },
          },
          '& pre': {
            backgroundColor: '#f4f4f4',
            padding: '1em',
            borderRadius: '4px',
            overflowX: 'auto',
            fontFamily: 'monospace',
          },
          '& code': {
            fontFamily: 'monospace',
            backgroundColor: '#f4f4f4',
            padding: '0.2em 0.4em',
            borderRadius: '3px',
          },
          '& .task-list': {
            listStyle: 'none',
            padding: 0,
          },
          '& .task-item': {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            margin: '0.5em 0',
          },
          '& blockquote': {
            borderLeft: '4px solid #ddd',
            paddingLeft: '1em',
            margin: '1em 0',
            color: '#666',
            fontStyle: 'italic',
          },
          '& a': {
            color: '#1976d2',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            margin: '1em 0 0.5em',
            fontWeight: 'bold',
          },
          '& p': {
            margin: '0.5em 0',
            lineHeight: 1.6,
          },
        },
      }}>
        <EditorContent editor={editor} />
      </Box>

      {/* Link Dialog */}
      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Insert Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="URL"
            type="url"
            fullWidth
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://example.com"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
          <Button onClick={addLink} variant="contained">Insert Link</Button>
        </DialogActions>
      </Dialog>

      {/* Image Dialog */}
      <Dialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Insert Image</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Box>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ mb: 1 }}
              >
                Upload Image from Computer
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => setImageFile(e.target.files[0])}
                />
              </Button>
            </Box>
            <MuiTypography align="center" color="textSecondary">OR</MuiTypography>
            <TextField
              margin="dense"
              label="Image URL"
              type="url"
              fullWidth
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialogOpen(false)}>Cancel</Button>
          <Button onClick={addImage} variant="contained">Insert Image</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AdvancedRichTextEditor;